/**
 * The profile's Rewards tab (own profile only): everything chests have given, in collapsible cards so a
 * phone keeps its length. Blockari's `shell/Rewards.tsx`, in 21 Wild's DOM and tokens:
 *
 *   Overview         tickets held, chests earned and opened, $CHAIN from chests (lifetime)
 *   Bonuses          the XP boost (multiplier, games left, no expiry) and free-entry tickets
 *   Frames & titles  what is owned; tap to equip, tap the equipped one to take it off
 *   History          opened chests, newest first, 50 a page, with what each gave
 *
 * Numbers come from `/api/me/rewards` (server `rewardsOf` defines each). Chests are earned, never bought:
 * nothing on this tab spends anything.
 */

import { chestLabel, chestSubline } from '@/game/chest-label';
import { COSMETICS, cosmetic, type ChestItem, type ChestSource, type Cosmetic } from '@/game/chests';
import type { Inventory, RewardEntry, Rewards } from '@/net/api';
import { formatScore } from '@/ui/hud';
import { coin } from '@/ui/screens';

export interface RewardsApi {
  load(cursor?: string): Promise<{ ok: true; value: Rewards } | { ok: false; error: string }>;
  inventory(): Promise<{ ok: true; value: Inventory } | { ok: false; error: string }>;
  equip(kind: 'frame' | 'title', itemId: string | null): Promise<{ ok: true; value: Inventory } | { ok: false; error: string }>;
}

/**
 * A history row's title: the caps label. "2ND PLACE \u00b7 WEEKLY $CHAIN" is two pieces, each kept whole, and the dot
 * between them is drawn by the styles (ui.css `.w21-pf-h-dotted`) so it can never end a line.
 */
export const titleParts = (label: string): string[] => label.split(' \u00b7 ');

export function historyTitle(label: string): HTMLElement {
  const parts = titleParts(label);
  const long = label.length > LONG_LABEL ? 'w21-pf-h-long' : '';
  if (parts.length < 2) return el('b', long || undefined, label);
  const b = el('b', `w21-pf-h-dotted ${long}`.trim());
  for (const part of parts) b.append(el('span', undefined, part));
  return b;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** One reward line: "+25 $CHAIN", "2× 10 ticket", "2× XP · 3 games", "Brass Rail frame". */
export function describeItem(item: ChestItem): string {
  switch (item.type) {
    case 'chain':
      return `+${formatScore(item.amount)} $CHAIN`;
    case 'ticket':
      return `${item.count}× ${item.fee} ticket`;
    case 'boost':
      return `${item.multiplier}× XP · ${item.games} games`;
    case 'cosmetic':
      return `${cosmetic(item.id)?.name ?? item.id} ${item.kind}`;
  }
}

function card(title: string, chip: string | number | null, open: boolean, body: HTMLElement): HTMLElement {
  const d = el('details', 'w21-pf-card');
  d.open = open;
  const sum = el('summary');
  sum.append(el('span', 'w21-pf-card-title', title));
  if (chip !== null) sum.append(el('span', 'w21-chip', String(chip)));
  const caret = el('i', 'w21-pf-caret');
  caret.setAttribute('aria-hidden', 'true');
  sum.append(caret);
  // Blockari's `.pf-card-body` is a wrapper around the content, so the tile grid keeps its own 8px gap.
  let wrap = body;
  if (body.className) {
    wrap = el('div');
    wrap.append(body);
  }
  wrap.classList.add('w21-pf-card-body');
  d.append(sum, wrap);
  return d;
}

/**
 * What a stat tile's figure says: the number, and (for $CHAIN) its unit. `n` sizes the figure to the tile: the unit,
 * smaller and with its space, counts as about four more characters. Blockari's `Tile` (c258375).
 */
export function tileFigure(value: number, chain: boolean): { text: string; unit: string | null; n: number } {
  const text = formatScore(value);
  return { text, unit: chain ? '$CHAIN' : null, n: text.length + (chain ? 4 : 0) };
}

/**
 * One stat tile. A $CHAIN figure reads "13 $CHAIN": the number, a real space, then the unit in a `<small>` (0.6em in
 * the label colour, from the lifted `.stat b small` rule), so the tile's text and a screen reader both say it
 * as a sentence rather than "13$CHAIN".
 */
function tile(label: string, value: number, chain = false): HTMLElement {
  const t = el('div', 'w21-stat');
  const f = tileFigure(value, chain);
  const b = el('b', undefined, f.text);
  b.style.setProperty('--n', String(f.n));
  if (f.unit) b.append(document.createTextNode(' '), el('small', undefined, f.unit));
  t.append(el('span', 'w21-t-label', label), b);
  return t;
}

const RARITY_COLOR: Record<string, string> = { common: '#bfc8e2', rare: '#4fb3ff', epic: '#d873ff', legendary: '#ffc53d' };
/**
 * A chest label longer than this takes the history row's first line to itself (ui.css `.w21-pf-h-long`); the rarity tag
 * and the time sit together under it. Set so every label, beside the widest rarity tag and an old date, fits a 360 px
 * phone without an ellipsis ("WELCOME GIFT" and the other 12-13 character labels included; mirrors Blockari 7f66e11).
 */
export const LONG_LABEL = 9;
const tc = (rarity: string): string => RARITY_COLOR[rarity] ?? RARITY_COLOR['common']!;

function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (!Number.isFinite(s)) return '';
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/** Build the tab into `host` and load it. Safe to call again: it starts over. */
export function mountRewards(host: HTMLElement, api: RewardsApi): void {
  host.replaceChildren(el('p', 'w21-t-caption', 'Loading…'));
  void api.load().then(async (res) => {
    if (!res.ok) {
      host.replaceChildren(el('p', 'w21-form__error', 'Could not load your rewards.'));
      return;
    }
    const inv = await api.inventory();
    render(host, api, res.value, inv.ok ? inv.value : null);
  });
}

function render(host: HTMLElement, api: RewardsApi, r: Rewards, inv0: Inventory | null): void {
  let inventory = inv0;
  let history: RewardEntry[] = [...r.history];
  let next = r.next;

  const overview = el('div', 'w21-stats w21-pf-tiles');
  overview.append(
    tile('Tickets', r.ticketCount),
    tile('Chests earned', r.chestsEarned),
    tile('Chests opened', r.chestsOpened),
    tile('From chests', r.chainFromChests, true),
  );

  /* -- bonuses ------------------------------------------------------------- */
  const bonusBody = el('div');
  if (r.bonuses.length === 0 && r.tickets.length === 0) {
    bonusBody.append(el('p', 'w21-t-caption', 'No active bonuses. Chests can hold XP boosts and free-entry tickets.'));
  } else {
    const list = el('ul', 'w21-pf-list');
    for (const b of r.bonuses) {
      const li = el('li', 'w21-pf-bonus');
      li.append(
        el('b', undefined, `${b.multiplier}\u00d7 XP`),
        el('span', undefined, `Active \u00b7 ${b.usesLeft} ${b.usesLeft === 1 ? 'game' : 'games'} left${b.expires ? ` \u00b7 until ${new Date(b.expires).toLocaleDateString()}` : ' \u00b7 no expiry'}`),
      );
      list.append(li);
    }
    for (const t of r.tickets) {
      const li = el('li', 'w21-pf-bonus');
      const b = el('b');
      b.append(document.createTextNode(`${t.fee} `), el('small', undefined, '$CHAIN'), document.createTextNode(' ticket'));
      li.append(b, el('span', undefined, `Free entry \u00b7 ${t.count} left \u00b7 no expiry`));
      list.append(li);
    }
    bonusBody.append(list);
  }

  /* -- frames & titles: the whole collection, owned ones equippable --------- */
  const collection = el('section', 'w21-cosmetics-panel');
  const paintCollection = (): void => {
    const owned = new Set([...r.frames.map((f) => f.id), ...r.titles.map((t) => t.id)]);
    const group = (kind: 'frame' | 'title'): HTMLElement => {
      const grid = el('div', `w21-cz-grid ${kind === 'frame' ? 'w21-frames' : 'w21-titles'}`);
      for (const c of COSMETICS.filter((x: Cosmetic) => x.kind === kind)) {
        const has = owned.has(c.id);
        const on = inventory !== null && inventory[kind] === c.id;
        const btn = el('button', `w21-cosmetic w21-${kind}${has ? '' : ' w21-locked'}${on ? ' w21-on' : ''}`) as HTMLButtonElement;
        btn.type = 'button';
        btn.style.setProperty('--tc', tc(c.rarity));
        btn.disabled = !has;
        btn.setAttribute('aria-pressed', String(on));
        btn.title = has ? c.name : `${c.name} \u00b7 from chests`;
        if (kind === 'frame') {
          const sw = el('span', `w21-frame-swatch w21-f-${c.id}`);
          sw.setAttribute('aria-hidden', 'true');
          btn.append(sw);
        } else {
          btn.append(el('span', 'w21-cz-quote', '\u201c'));
        }
        btn.append(el('span', 'w21-cz-name', c.name), el('span', 'w21-cz-state', on ? 'EQUIPPED' : has ? 'EQUIP' : c.rarity.toUpperCase()));
        btn.addEventListener('click', () => {
          if (!has) return;
          for (const x of collection.querySelectorAll('button')) x.disabled = true;
          void api.equip(kind, on ? null : c.id).then((res) => {
            if (res.ok) inventory = res.value;
            paintCollection();
          });
        });
        grid.append(btn);
      }
      return grid;
    };
    collection.replaceChildren(group('frame'), group('title'));
  };
  paintCollection();

  /* -- history ------------------------------------------------------------- */
  const historyBody = el('div');
  const paintHistory = (): void => {
    historyBody.replaceChildren();
    if (history.length === 0) {
      const empty = el('div', 'w21-empty');
      empty.append(el('h3', undefined, 'No chests opened yet'), el('p', undefined, 'Win challenges and climb ranks to earn chests. They are never for sale.'));
      historyBody.append(empty);
      return;
    }
    const ol = el('ol', 'w21-pf-history');
    for (const h of history) {
      const li = el('li', `r-${h.rarity}`);
      li.style.setProperty('--tc', tc(h.rarity));
      const head = el('div', 'w21-pf-h-head');
      const time = el('time', undefined, ago(h.openedAt));
      time.dateTime = h.openedAt;
      time.title = new Date(h.openedAt).toLocaleString();
      const ref = { source: h.source as ChestSource, sourceRef: h.sourceRef };
      const label = chestLabel(ref);
      head.append(historyTitle(label), el('span', 'w21-pf-h-rarity', h.rarity), time);
      const sub = chestSubline(ref);
      const items = el('div', 'w21-pf-h-items');
      for (const c of h.contents) {
        const chip = el('span', `w21-pf-item w21-${c.type}`);
        if (c.type === 'chain') chip.append(coin());
        chip.append(document.createTextNode(describeItem(c)));
        items.append(chip);
      }
      li.append(head, ...(sub ? [el('div', 'w21-pf-h-week', sub)] : []), items);
      ol.append(li);
    }
    historyBody.append(ol);
    if (next) {
      const more = el('button', 'w21-btn w21-block', 'Show older') as HTMLButtonElement;
      more.type = 'button';
      more.addEventListener('click', () => {
        more.disabled = true;
        more.textContent = 'Loading';
        void api.load(next ?? undefined).then((res) => {
          if (res.ok) {
            history = [...history, ...res.value.history];
            next = res.value.next;
          }
          paintHistory();
        });
      });
      historyBody.append(more);
    }
  };
  paintHistory();

  const root = el('div', 'w21-pf-rewards');
  root.setAttribute('aria-label', 'Rewards');
  root.append(
    card('Overview', null, true, overview),
    card('Bonuses', r.bonuses.length + (r.tickets.length > 0 ? 1 : 0), r.bonuses.length > 0 || r.tickets.length > 0, bonusBody),
    card('Frames & titles', r.frames.length + r.titles.length, false, collection),
    card('History', r.chestsOpened, true, historyBody),
  );
  host.replaceChildren(root);
}
